// src/pages/Godown/ViewGodowns.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import godownService from '../../services/godown.service';
import Toast from '../../components/Toast/Toast';
import '../../styles/ViewPages.css';

const ViewGodowns = () => {
  const navigate = useNavigate();

  const [godowns, setGodowns]         = useState([]);
  const [searchTerm, setSearchTerm]   = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [loading, setLoading]         = useState(false);
  const [toast, setToast]             = useState(null);

  useEffect(() => { fetchGodowns(); }, [currentPage, searchTerm]);

  const fetchGodowns = async () => {
    setLoading(true);
    try {
      // ✅ Godowns are global — no companyId needed
      const res = await godownService.getGodowns(currentPage, 10, searchTerm);
      if (res.success && res.data) {
        setGodowns(res.data.godowns || []);
        setTotalPages(res.data.pagination?.totalPages || 1);
      }
    } catch (err) {
      console.error('Error fetching godowns:', err);
      setToast({ message: 'Failed to fetch godowns', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    try {
      const res = await godownService.deleteGodown(id);
      if (res.success) {
        setToast({ message: 'Godown deleted successfully!', type: 'success' });
        fetchGodowns();
      }
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Failed to delete godown',
        type: 'error',
      });
    }
  };

  const renderRegions = (regions) => {
    if (!regions || regions.length === 0) return '—';
    // Show region — company to disambiguate when same region name exists across companies
    const label = (r) => r.company?.name ? `${r.name} (${r.company.name})` : r.name;
    if (regions.length <= 2) return regions.map(label).join(', ');
    return (
      <span title={regions.map(label).join(', ')}>
        {regions.slice(0, 2).map(label).join(', ')}
        <span style={{ color: '#6b7280', fontSize: 12 }}> +{regions.length - 2} more</span>
      </span>
    );
  };

  return (
    <div className="view-godowns-container">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="view-header">
        <h4 className="view-title">View Godowns</h4>
        <div className="view-actions">
          <button className="btn-add" onClick={() => navigate('/godown/add')}>
            + Add Godown
          </button>
        </div>
      </div>

      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search godowns by name, location... 🔍"
          value={searchTerm}
          onChange={handleSearch}
        />
      </div>

      <div className="table-container">
        {loading ? (
          <div className="loading">Loading godowns...</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>GODOWN NAME</th>
                <th>LOCATION</th>
                <th>REGIONS</th>
                <th>MANAGER</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {godowns.length > 0 ? godowns.map(g => (
                <tr key={g.id}>
                  <td>{g.name}</td>
                  <td>{g.location}</td>
                  <td>{renderRegions(g.regions)}</td>
                  <td>{g.manager || 'N/A'}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-view" onClick={() => navigate(`/godown/view/${g.id}`)} title="View Details">view</button>
                      <button className="btn-edit" onClick={() => navigate(`/godown/edit/${g.id}`)} title="Edit Godown">✏️</button>
                      <button className="btn-delete" onClick={() => handleDelete(g.id, g.name)} title="Delete Godown">🗑️</button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5" className="no-data">
                    {searchTerm ? 'No godowns found matching your search' : 'No godowns available'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button className="pagination-btn" onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>« Previous</button>
          <span className="pagination-info">Page {currentPage} of {totalPages}</span>
          <button className="pagination-btn" onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>Next »</button>
        </div>
      )}
    </div>
  );
};

export default ViewGodowns;